import type TauriDatabase from "@tauri-apps/plugin-sql";
import { type Database as BunDatabase } from "bun:sqlite";

import { type SqliteColumnType, type SqliteTableDef } from "../schema";
import { runCheckTableExists } from "./runCheckTableExists";
import { runCreateTable } from "./runCreateTable";
import { runGetTableXInfo } from "./runGetTableXInfo";
import { upsertSql } from "./upsertSql";

/**
 * Creates a table in the database if it doesn't already exist.
 * If the table was newly created and defaultData is provided, inserts the default data.
 * @param db The database connection (either Bun's SQLite or Tauri's SQL plugin)
 * @param tableDef The table definition to create
 * @param defaultData Optional array of default records to insert if the table is newly created
 */
export async function runCreateTableIfNotExists(
	db: BunDatabase | TauriDatabase,
	tableDef: SqliteTableDef,
	defaultData?: unknown[]
): Promise<void> {
	// Check if table exists using our dedicated function
	const tableExistedPreviously = await runCheckTableExists(db, tableDef.tableName);

	await runCreateTable(db, tableDef, true);

	if (tableExistedPreviously || !defaultData || defaultData.length === 0) {
		return;
	}

	//
	// Create the table if it doesn't exist
	//

	try {
		const tableInfo = await runGetTableXInfo(db, tableDef.tableName);

		const tableDefForUpsert: SqliteTableDef = {
			tableName: tableDef.tableName,
			columns: tableInfo.columns.map((col) => ({
				name: col.name,
				type: col.type as SqliteColumnType,
				nullable: col.notnull === 0,
				primaryKey: col.pk === 1,
				unique: col.pk === 1,
			})),
		};

		for (const record of defaultData) {
			const data = record as Record<string, unknown>;
			const { statement, params } = upsertSql(tableDefForUpsert, data);
			if ("execute" in db) {
				await db.execute(statement, params);
			} else {
				db.prepare(statement, params).run();
			}
		}
	} catch (e) {
		console.error(
			"Failed to initialize table with data:",
			tableDef.tableName,
			tableDef.columns,
			e
		);
	}
}
